/**
 * WebSocket signaling connection for P2P WebRTC setup.
 *
 * IMPORTANT: This class only handles signaling (connection setup). It does NOT
 * carry gameplay messages. Once the RTCDataChannel opens, all gameplay traffic
 * flows over the data channel, not this WebSocket.
 *
 * The class is exported as `WebSocketSignalingConnection` (the correct name)
 * and `WebSocketRelayConnection` (legacy compatibility alias). The legacy name
 * will be removed in a future refactor.
 */

import type { P2PMessage } from "../engine/types";
import type { IP2PConnection, ConnectionState, ConnectionEvents } from "./base";
import { serializeMessage, deserializeMessage } from "./protocol";
import { SIGNALING_URL, RTC_CONFIG } from "../config/constants";
import { type SignalingMessage, assertSignalingOnly, isRelayCandidate, classifyCandidate } from "./signaling";
export type { ConnectionState, ConnectionEvents };

/** Signaling roles. */
export type SignalingRole = "host" | "joiner";

/** Connection-specific errors with precise failure reasons. */
export class SignalingConnectionError extends Error {
  constructor(
    message: string,
    public readonly reason: SignalingFailureReason,
  ) {
    super(message);
    this.name = "SignalingConnectionError";
  }
}

export type SignalingFailureReason =
  | "signaling-unavailable"
  | "room-not-found"
  | "room-full"
  | "host-cancelled"
  | "peer-left"
  | "offer-answer-failed"
  | "ice-failed"
  | "data-channel-failed"
  | "relay-candidate-rejected"
  | "timeout"
  | "unknown";

const JOINER_TIMEOUT_MS = 60000;
const ICE_GATHER_TIMEOUT_MS = 10000;
const PING_INTERVAL_MS = 10000;

/** Human-readable error messages for each failure reason. */
const ERROR_MESSAGES: Record<SignalingFailureReason, string> = {
  "signaling-unavailable": "Could not reach signaling service. Direct P2P setup cannot start.",
  "room-not-found": "Room not found or host is no longer waiting.",
  "room-full": "Room already has two players.",
  "host-cancelled": "Host cancelled the room before you connected.",
  "peer-left": "The other peer left before the connection was established.",
  "offer-answer-failed": "Direct peer setup failed during offer/answer exchange.",
  "ice-failed": "Direct peer connection failed. ICE could not establish a path.",
  "data-channel-failed": "Direct peer connection failed. The data channel could not open.",
  "relay-candidate-rejected": "Relay ICE candidate rejected to preserve strict P2P mode.",
  "timeout": "No peer joined within the timeout.",
  "unknown": "Direct peer connection failed for an unknown reason.",
};

export function getSignalingErrorMessage(reason: SignalingFailureReason): string {
  return ERROR_MESSAGES[reason] ?? ERROR_MESSAGES.unknown;
}

export class WebSocketSignalingConnection implements IP2PConnection {
  private events: ConnectionEvents;
  private state: ConnectionState = "disconnected";
  private url: string;
  private ws: WebSocket | null = null;
  private room: string | null = null;
  private role: SignalingRole = "joiner";
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private iceCandidates: RTCIceCandidateInit[] = [];
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private offerSent = false;
  private joinTimer: ReturnType<typeof setTimeout> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private dataChannelOpenResolver: (() => void) | null = null;
  private dataChannelOpenRejecter: ((reason: SignalingFailureReason) => void) | null = null;
  private hasPeer = false;
  private wasCancelled = false;
  private localPeerId: string;

  constructor(events: ConnectionEvents, url = SIGNALING_URL) {
    this.events = events;
    this.url = url;
    this.localPeerId = generatePeerId();
  }

  getState(): ConnectionState {
    return this.state;
  }

  getPeerIds(): string[] {
    return this.dc?.readyState === "open" ? [this.localPeerId === "host" ? "joiner" : "host"] : [];
  }

  getPeerId(): string | null {
    return this.dc?.readyState === "open" ? (this.localPeerId === "host" ? "joiner" : "host") : null;
  }

  getLocalPeerId(): string {
    return this.localPeerId;
  }

  async join(room: string, roleOrIsHost: SignalingRole | boolean = "joiner"): Promise<void> {
    if (this.ws) throw new Error("Already connected");
    const role = typeof roleOrIsHost === "boolean" ? (roleOrIsHost ? "host" : "joiner") : roleOrIsHost;
    this.room = room;
    this.role = role;
    this.localPeerId = role;
    this.wasCancelled = false;
    this.setState("connecting");
    this.log("role", role, "room", room, "url", this.url);

    return new Promise((resolve, reject) => {
      const wsUrl = `${this.url}?room=${encodeURIComponent(room)}`;
      this.log("opening signaling socket to", wsUrl);
      const ws = new WebSocket(wsUrl);
      this.ws = ws;

      ws.onopen = () => {
        this.log("signaling socket open");
        this.sendSignaling({ type: "join", room, role });
        this.startPing();
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data as string) as SignalingMessage;
          this.handleSignalingMessage(msg);
        } catch (err) {
          this.log("invalid signaling frame", err);
        }
      };

      ws.onerror = (_err) => {
        this.stopPing();
        this.setState("error");
        reject(new SignalingConnectionError(getSignalingErrorMessage("signaling-unavailable"), "signaling-unavailable"));
      };

      ws.onclose = () => {
        this.stopPing();
        if (this.state !== "connected" && this.state !== "error") {
          this.setState("disconnected");
        }
        if (!this.hasPeer && this.state !== "connected" && !this.wasCancelled) {
          reject(new SignalingConnectionError(getSignalingErrorMessage("host-cancelled"), "host-cancelled"));
        }
      };

      // Host waits indefinitely; joiner times out after JOINER_TIMEOUT_MS
      if (role === "joiner") {
        this.joinTimer = setTimeout(() => {
          this.cleanup();
          reject(new SignalingConnectionError(getSignalingErrorMessage("timeout"), "timeout"));
        }, JOINER_TIMEOUT_MS);
      }

      // Resolve once the data channel is open
      this.dataChannelOpenResolver = () => {
        if (this.joinTimer) clearTimeout(this.joinTimer);
        resolve();
      };
      this.dataChannelOpenRejecter = (reason) => {
        if (this.joinTimer) clearTimeout(this.joinTimer);
        reject(new SignalingConnectionError(getSignalingErrorMessage(reason), reason));
      };
    });
  }

  async send(msg: P2PMessage): Promise<void> {
    if (!this.dc || this.dc.readyState !== "open") throw new Error("Data channel not open");
    this.log("first gameplay message sent over data channel:", msg.type);
    this.dc.send(serializeMessage(msg));
  }

  async broadcast(msg: P2PMessage): Promise<void> {
    await this.send(msg);
  }

  async leave(): Promise<void> {
    this.wasCancelled = true;
    this.sendSignaling({ type: "leave", room: this.room ?? "" });
    this.cleanup();
  }

  private cleanup(): void {
    this.stopPing();
    if (this.joinTimer) {
      clearTimeout(this.joinTimer);
      this.joinTimer = null;
    }
    this.dc?.close();
    this.pc?.close();
    this.ws?.close();
    this.dc = null;
    this.pc = null;
    this.ws = null;
    this.iceCandidates = [];
    this.pendingCandidates = [];
    this.offerSent = false;
    this.hasPeer = false;
    this.dataChannelOpenResolver = null;
    this.dataChannelOpenRejecter = null;
    this.setState("disconnected");
  }

  private startPing(): void {
    this.stopPing();
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.sendSignaling({ type: "ping", room: this.room ?? "" });
      }
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private sendSignaling(msg: SignalingMessage): void {
    assertSignalingOnly(msg);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  private handleSignalingMessage(msg: SignalingMessage): void {
    this.log("signaling recv", msg.type);
    switch (msg.type) {
      case "joined":
        // Server confirmed our join; nothing else to do until peer joins
        break;
      case "peer-joined":
        this.hasPeer = true;
        if (this.role === "host" && !this.offerSent) {
          void this.createOfferAndSend();
        }
        break;
      case "peer-left":
        this.hasPeer = false;
        if (this.state !== "connected") {
          this.dataChannelOpenRejecter?.("peer-left");
        } else {
          this.events.onPeerLeave(this.role === "host" ? "joiner" : "host");
        }
        break;
      case "room-full":
        this.dataChannelOpenRejecter?.("room-full");
        this.cleanup();
        break;
      case "room-not-found":
        this.dataChannelOpenRejecter?.("room-not-found");
        this.cleanup();
        break;
      case "error":
        this.log("signaling error from server:", msg.reason);
        this.dataChannelOpenRejecter?.("unknown");
        this.cleanup();
        break;
      case "offer":
        void this.handleOffer(msg);
        break;
      case "answer":
        void this.handleAnswer(msg);
        break;
      case "ice-candidate":
        void this.handleRemoteIceCandidate(msg.candidate);
        break;
      case "pong":
        // Keepalive response
        break;
      case "ping":
        this.sendSignaling({ type: "pong", room: this.room ?? "" });
        break;
      case "leave":
        if (this.state !== "connected") {
          this.dataChannelOpenRejecter?.("host-cancelled");
        }
        this.cleanup();
        break;
      default:
        break;
    }
  }

  private createPeerConnection(): RTCPeerConnection {
    const pc = new RTCPeerConnection(RTC_CONFIG);
    this.pc = pc;
    this.log("created RTCPeerConnection with iceServers:", RTC_CONFIG.iceServers?.map((s) => s.urls).join(", "));

    pc.onsignalingstatechange = () => {
      this.log("signalingState ->", pc.signalingState);
    };

    pc.onicegatheringstatechange = () => {
      this.log("iceGatheringState ->", pc.iceGatheringState);
    };

    pc.oniceconnectionstatechange = () => {
      this.log("iceConnectionState ->", pc.iceConnectionState);
      if (pc.iceConnectionState === "failed") {
        this.dataChannelOpenRejecter?.("ice-failed");
      }
    };

    pc.onconnectionstatechange = () => {
      this.log("connectionState ->", pc.connectionState);
      switch (pc.connectionState) {
        case "connecting":
          this.setState("connecting");
          break;
        case "connected":
          this.setState("connected");
          break;
        case "disconnected":
        case "failed":
        case "closed":
          if (this.state === "connected") {
            this.setState("disconnected");
            this.events.onPeerLeave(this.role === "host" ? "joiner" : "host");
          }
          break;
      }
    };

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        if (isRelayCandidate(event.candidate)) {
          this.log("rejected local relay candidate:", classifyCandidate(event.candidate));
          return;
        }
        this.log("local ICE candidate discovered:", classifyCandidate(event.candidate));
        this.iceCandidates.push(event.candidate.toJSON());
        if (this.room) {
          this.sendSignaling({ type: "ice-candidate", room: this.room, candidate: event.candidate.toJSON() });
        }
      } else {
        this.log("local ICE gathering complete; candidates:", this.iceCandidates.length);
      }
    };

    return pc;
  }

  private async createOfferAndSend(): Promise<void> {
    if (!this.room || this.offerSent) return;
    this.offerSent = true;
    try {
      const pc = this.createPeerConnection();
      this.dc = pc.createDataChannel("pbs-messages", { ordered: true });
      this.log("dataChannel created");
      this.setupDataChannel(this.dc);

      const offer = await pc.createOffer();
      this.log("offer created");
      await pc.setLocalDescription(offer);
      await this.waitForIceComplete();

      this.log("offer sent");
      this.sendSignaling({
        type: "offer",
        room: this.room,
        sdp: pc.localDescription!.sdp,
        candidates: this.iceCandidates,
      });
    } catch (err) {
      this.log("offer creation failed", err);
      this.dataChannelOpenRejecter?.("offer-answer-failed");
    }
  }

  private async handleOffer(msg: { sdp: string; candidates: RTCIceCandidateInit[] }): Promise<void> {
    if (this.offerSent) return; // avoid glare; we already initiated
    try {
      const pc = this.createPeerConnection();

      pc.ondatachannel = (event) => {
        this.dc = event.channel;
        this.log("dataChannel received from offer");
        this.setupDataChannel(this.dc);
      };

      this.log("offer received");
      await pc.setRemoteDescription({ type: "offer", sdp: msg.sdp });
      this.log("remoteDescription set (offer)");
      await this.applyRemoteCandidates(msg.candidates);

      const answer = await pc.createAnswer();
      this.log("answer created");
      await pc.setLocalDescription(answer);
      await this.waitForIceComplete();

      if (this.room) {
        this.log("answer sent");
        this.sendSignaling({
          type: "answer",
          room: this.room,
          sdp: pc.localDescription!.sdp,
          candidates: this.iceCandidates,
        });
      }
    } catch (err) {
      this.log("answer creation failed", err);
      this.dataChannelOpenRejecter?.("offer-answer-failed");
    }
  }

  private async handleAnswer(msg: { sdp: string; candidates: RTCIceCandidateInit[] }): Promise<void> {
    if (!this.pc) return;
    try {
      this.log("answer received");
      await this.pc.setRemoteDescription({ type: "answer", sdp: msg.sdp });
      this.log("remoteDescription set (answer)");
      await this.applyRemoteCandidates(msg.candidates);
    } catch (err) {
      this.log("answer handling failed", err);
      this.dataChannelOpenRejecter?.("offer-answer-failed");
    }
  }

  private async handleRemoteIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!this.pc) {
      if (isRelayCandidate(candidate)) {
        this.log("rejected buffered remote relay candidate:", classifyCandidate(candidate));
        return;
      }
      this.log("buffered remote ICE candidate:", classifyCandidate(candidate));
      this.pendingCandidates.push(candidate);
      return;
    }
    if (isRelayCandidate(candidate)) {
      this.log("rejected remote relay candidate:", classifyCandidate(candidate));
      return;
    }
    this.log("remote ICE candidate applied:", classifyCandidate(candidate));
    await this.pc.addIceCandidate(candidate).catch((err) => {
      this.log("failed to add remote ICE candidate", err);
    });
  }

  private async applyRemoteCandidates(candidates: RTCIceCandidateInit[]): Promise<void> {
    for (const candidate of candidates) {
      if (isRelayCandidate(candidate)) {
        this.log("rejected remote relay candidate in bundle:", classifyCandidate(candidate));
        continue;
      }
      this.log("remote ICE candidate from bundle:", classifyCandidate(candidate));
      await this.pc!.addIceCandidate(candidate).catch((err) => {
        this.log("failed to add bundled remote ICE candidate", err);
      });
    }
    // Also flush any candidates that arrived before remoteDescription was set
    for (const candidate of this.pendingCandidates) {
      if (isRelayCandidate(candidate)) continue;
      this.log("flushed buffered remote ICE candidate:", classifyCandidate(candidate));
      await this.pc!.addIceCandidate(candidate).catch((err) => {
        this.log("failed to add buffered remote ICE candidate", err);
      });
    }
    this.pendingCandidates = [];
  }

  private setupDataChannel(dc: RTCDataChannel): void {
    dc.onopen = () => {
      this.log("dataChannel open");
      this.setState("connected");
      this.events.onPeerJoin(this.role === "host" ? "joiner" : "host");
      this.dataChannelOpenResolver?.();
    };

    dc.onclose = () => {
      this.log("dataChannel close");
      this.setState("disconnected");
      this.events.onPeerLeave(this.role === "host" ? "joiner" : "host");
    };

    dc.onerror = (err) => {
      this.log("dataChannel error", err);
      this.dataChannelOpenRejecter?.("data-channel-failed");
    };

    dc.onmessage = (event) => {
      const msg = deserializeMessage(event.data);
      if (msg) {
        this.log("dataChannel recv", msg.type);
        this.events.onMessage(msg, this.role === "host" ? "joiner" : "host");
      }
    };
  }

  private async waitForIceComplete(): Promise<void> {
    if (!this.pc) return;
    return new Promise((resolve) => {
      const pc = this.pc!;
      if (pc.iceGatheringState === "complete") {
        resolve();
        return;
      }
      const handler = () => {
        if (pc.iceGatheringState === "complete") {
          pc.onicegatheringstatechange = null;
          resolve();
        }
      };
      pc.onicegatheringstatechange = handler;
      // Fallback timeout so we never wait forever for ICE
      setTimeout(() => {
        pc.onicegatheringstatechange = null;
        resolve();
      }, ICE_GATHER_TIMEOUT_MS);
    });
  }

  private setState(state: ConnectionState): void {
    this.log("state ->", state);
    this.state = state;
    this.events.onStateChange(state);
  }

  private log(...args: unknown[]): void {
    console.log(`[WebSocketSignaling]`, ...args);
  }
}

function generatePeerId(): string {
  return Math.random().toString(36).slice(2, 10);
}

/**
 * Legacy compatibility alias. This name will be removed in a future refactor.
 * TODO: Remove WebSocketRelayConnection export once all callers have migrated.
 */
export const WebSocketRelayConnection = WebSocketSignalingConnection;
